import type Metadata from './Metadata';
import type { Image3DPageData } from './sources';
import { ImageLikeSource, SourceType } from './sources';
import type { Onset, Pixel } from './Unit';
import type Unit from './Unit';
import PixelUtils from './PixelUtils';

export type LyricsEntry = {
  type: 'lyrics' | 'break';
  tag: string;
  start: number;
};

export type LyricsRender = {
  text: string;
  x: number;
  onset: number;
};

export type LyricsRowData = {
  y: number;
  renders: LyricsRender[];
};

export type LyricsRenderConfig = {
  fontSize: number;
  letterSpacing: number;
  wordSpacing: number;
};

export type LyricsCursorRect = {
  x: Pixel;
  y: Pixel;
  width: Pixel;
  height: Pixel;
};

type LyricsData = {
  lyricsEntries: LyricsEntry[];
  pixelUnit: Unit<Pixel>;
  width: Pixel;
  height: Pixel;
  pages?: Image3DPageData[];
  metadata: Metadata;
  name?: string;
  source?: string;
  renderRows: LyricsRowData[];
  renderConfig: LyricsRenderConfig;
  isLinear: boolean;
};

export default class Lyrics extends ImageLikeSource implements Readonly<LyricsData> {
  readonly lyricsEntries: LyricsEntry[];

  readonly renderRows: LyricsRowData[];

  readonly renderConfig: LyricsRenderConfig;

  protected constructor(parsed: LyricsData) {
    super({
      type: SourceType.Lyrics,
      pixelUnit: parsed.pixelUnit,
      width: parsed.width,
      height: parsed.height,
      pages: parsed.pages,
      metadata: parsed.metadata,
      name: parsed.name,
      source: parsed.source,
      isLinear: false,
    });
    this.lyricsEntries = parsed.lyricsEntries;
    this.renderRows = parsed.renderRows;
    this.renderConfig = parsed.renderConfig;
  }

  getCursorRect(musicalTime: Onset): LyricsCursorRect | null {
    if (!this.pages || !this.pixelUnit || !this.lyricsEntries || this.lyricsEntries.length === 0) {
      return null;
    }

    /*
       This constant OFFSET is used to adjust the x position of the cursor rectangle.
       It serves as a temporary fix to ensure that the cursor rectangle aligns correctly
       with the lyrics entries on the page. It is used to cancel the margin applied when
       the positions data are computed. This margin is needed to draw the labels correctly,
       but it causes the cursor rectangle to be misaligned.
       The value of 7 was determined empirically to provide a good visual alignment.
       In fact the actual margin is 8 (quarter of the font size, which is 24),
       but 7 was found to be a better fit for the cursor rectangle.
       However, this is not a permanent solution and should be revisited if the
       underlying issue with the margin is resolved.
       -> https://gitlab.com/algomus.fr/dezrann/dezrann-front/-/work_items/886
    */
    const OFFSET = 7;
    let currentLyricIndex = -1;
    let left = 0;
    let right = this.lyricsEntries.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      if (this.lyricsEntries[mid].start <= musicalTime) {
        currentLyricIndex = mid;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    if (currentLyricIndex === -1) {
      return null;
    }

    const currentLyric = this.lyricsEntries[currentLyricIndex];

    let nextOnset = currentLyric.start + 1;
    if (currentLyricIndex < this.lyricsEntries.length - 1) {
      nextOnset = this.lyricsEntries[currentLyricIndex + 1].start;
    }

    const startPixel = this.pixelUnit.fromOnset(currentLyric.start as Onset).value;
    const endPixel = this.pixelUnit.fromOnset(nextOnset as Onset).value;

    const startPos = PixelUtils.deMux(startPixel);
    const endPos = PixelUtils.deMux(endPixel);

    const x = startPos.x - OFFSET as Pixel;
    let width: Pixel;

    if (startPos.row === endPos.row) {
      width = (endPos.x - startPos.x) as Pixel;
    } else {
      const rowDef = this.pages[startPos.page].rows[startPos.row];
      width = (rowDef.end - x) as Pixel;
    }

    const currentRowY = this.pages[startPos.page].rows[startPos.row].y;
    const currentPageY = this.pages[startPos.page].height * startPos.page;
    const y = (currentPageY + currentRowY) as Pixel;
    const { height } = this.pages[startPos.page].rows[startPos.row];

    return {
      x, y, width, height,
    };
  }

  scaled(scale: number): Lyrics {
    const data = { ...this };
    return new Lyrics({
      ...data,
      width: data.width * scale,
      pixelUnit: data.pixelUnit.getScaled(scale),
    });
  }
}
